import { Button } from '@mui/material'
import { FC, useCallback, useState } from 'react'
import { generatePath } from 'react-router-dom'
import { InformationModal } from 'utoniq-ui'

import { Maybe, PaymentMethod } from '@/clients/utoniq-core/schema'
import { useMessage, useRouter, useTranslation } from '@/core'
import { captureError } from '@/core/errors'
import { isCarrierPaymentMethod } from '@/lib/escott'

import { Action } from './../'

type CancelChangePlanButtonProps = {
  type: string
  artistId: string
  paymentMethod?: Maybe<PaymentMethod>
  cancellingChangeSubscription: boolean
  onCancelChangeSubscription: Action['onCancelChangeSubscription']
}

export const CancelChangePlanButton: FC<CancelChangePlanButtonProps> = ({
  type,
  artistId,
  paymentMethod,
  cancellingChangeSubscription,
  onCancelChangeSubscription,
}) => {
  const { t } = useTranslation('membership')
  const { routerPages } = useRouter()
  const { showError } = useMessage()
  const [openConfirm, setOpenConfirm] = useState<boolean>(false)

  const handleCancelChangeSubscription = useCallback(async () => {
    if (!paymentMethod) return

    return onCancelChangeSubscription(artistId, paymentMethod, {
      // 外部キャリア決済画面での処理が成功した場合のリダイレクト先
      successUrl: `${window.location.origin}${generatePath(
        routerPages.MembershipManagement
      )}`,
      // 外部キャリア決済画面での処理がキャンセルされた場合のリダイレクト先
      cancelUrl: `${window.location.origin}${generatePath(
        routerPages.MembershipManagement
      )}`,
      // 外部キャリア決済画面での処理がエラーになった場合のリダイレクト先
      errorUrl: `${window.location.origin}${generatePath(
        routerPages.MembershipManagement
      )}`,
    })
      .catch(e => {
        captureError(e, 'MembershipTemplate')
        showError(new Error(t('error')))
      })
      .finally(() => {
        // mutation の完了から外部キャリア決済画面に遷移するまでの間に
        // 画面内で操作が行われてしまうことを防ぐため
        // キャリア決済の場合はモーダルを閉じない
        if (!isCarrierPaymentMethod(paymentMethod)) setOpenConfirm(false)
      })
  }, [artistId, paymentMethod, onCancelChangeSubscription, showError, t])

  if (type !== 'stripe' && type !== 'escott') {
    return null
  }

  return (
    <>
      <Button
        size="large"
        variant="contained"
        color="inherit"
        sx={{
          width: 240,
          '&.MuiButtonBase-root:hover': { bgcolor: 'grey.50' },
          bgcolor: 'grey.50',
        }}
        onClick={() => setOpenConfirm(true)}
      >
        {t('cancelChangePlan')}
      </Button>

      <InformationModal
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        title={t('confirm')}
        content={t('cancelChangeConfirmContent')}
        cancelText={t('cancel')}
        buttons={
          <Button
            size="large"
            sx={{ minWidth: 212 }}
            onClick={handleCancelChangeSubscription}
            loading={cancellingChangeSubscription}
            disabled={cancellingChangeSubscription}
          >
            {t('stopSubscription')}
          </Button>
        }
      />
    </>
  )
}
